import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageSEO from "@/components/feature/PageSEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieGrid from "@/components/feature/MovieGrid";
import EmptyState from "@/components/feature/EmptyState";
import Toast from "@/components/base/Toast";
import { useToast } from "@/hooks/useToast";
import { formatViews, getMovieById, getRelated } from "@/utils/movie";
import PlayerDemo from "@/pages/detail/components/PlayerDemo";
import EpisodeList from "@/pages/detail/components/EpisodeList";

export default function DetailPage() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);
  const { message, show } = useToast();
  const [episodeIndex, setEpisodeIndex] = useState(1);

  if (!movie) {
    return (
      <Layout>
        <PageSEO
          title="作品不存在 - 熊猫影院"
          description="抱歉，你访问的影视作品不存在或已下架，请返回首页浏览其他内容。"
          keywords="熊猫影院,作品不存在,影视资源"
          path={`/detail/${id}`}
        />
        <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
          <EmptyState
            title="找不到这部影视作品"
            description="你访问的作品可能已被移除或链接有误，返回首页继续浏览其他精彩内容吧。"
          />
          <div className="mt-6 flex justify-center">
            <Link
              to="/"
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-home-4-line text-base"></i>
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const related = getRelated(movie);
  const isTv = movie.type === "tv" && movie.episodes && movie.episodes.length > 0;
  const activeEpisode =
    isTv && movie.episodes ? movie.episodes.find((e) => e.index === episodeIndex) : undefined;

  return (
    <Layout>
      <PageSEO
        title={`${movie.title} - 熊猫影院详情介绍`}
        description={movie.summary}
        keywords={`${movie.title},熊猫影院,${movie.genres.join(",")},${movie.typeLabel},在线观看`}
        path={`/detail/${movie.id}`}
      />

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: movie.typeLabel, to: `/category/${movie.type}` },
            { label: movie.title },
          ]}
        />

        <div className="mt-6 flex flex-col gap-8 lg:flex-row">
          <aside className="order-2 w-full lg:order-1 lg:w-[320px]">
            <div className="lg:sticky lg:top-24">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 主封面`}
                  title={`${movie.title} 熊猫影院`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-5 rounded-lg border border-background-200/80 bg-background-100/70 p-5">
                <h2 className="text-sm font-semibold text-foreground-900">影视信息</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex gap-3">
                    <dt className="w-14 shrink-0 text-foreground-500">类型</dt>
                    <dd className="text-foreground-800">{movie.typeLabel}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-14 shrink-0 text-foreground-500">导演</dt>
                    <dd className="text-foreground-800">{movie.director}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-14 shrink-0 text-foreground-500">主演</dt>
                    <dd className="text-foreground-800">{movie.actors.join(" / ")}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-14 shrink-0 text-foreground-500">上映</dt>
                    <dd className="text-foreground-800">
                      {movie.year} 年 · {movie.updatedAt} 上线
                    </dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-14 shrink-0 text-foreground-500">地区</dt>
                    <dd className="text-foreground-800">{movie.region}</dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="w-14 shrink-0 text-foreground-500">片长</dt>
                    <dd className="text-foreground-800">{movie.duration}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex gap-3 border-t border-background-200/70 pt-5">
                  <button
                    type="button"
                    onClick={() => show("收藏功能尚未接入，当前为前端演示")}
                    className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                  >
                    <i className="ri-bookmark-line text-base"></i>
                    收藏
                  </button>
                  <button
                    type="button"
                    onClick={() => show("分享功能尚未接入，当前为前端演示")}
                    className="flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                  >
                    <i className="ri-share-forward-line text-base"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </aside>

          <div className="order-1 min-w-0 flex-1 lg:order-2">
            <header>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                  {movie.title}
                </h1>
                <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-sm font-bold text-foreground-950">
                  <i className="ri-star-fill text-xs"></i>
                  {movie.rating.toFixed(1)}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
                <span>{movie.year}</span>
                <span>{movie.region}</span>
                <span>{movie.duration}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-play-circle-line text-base"></i>
                  {formatViews(movie.views)}次播放
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${movie.type}?genre=${encodeURIComponent(g)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                  >
                    {g}
                  </Link>
                ))}
                {movie.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-background-300/70 px-3 py-1 text-xs text-foreground-600"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </header>

            <div className="mt-6">
              <PlayerDemo
                movie={movie}
                episodeLabel={activeEpisode ? activeEpisode.title : undefined}
              />
            </div>

            <section className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                <i className="ri-file-text-line text-xl text-primary-500"></i>
                剧情简介
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
                {movie.summary}
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                本作品由 <strong className="text-foreground-800">{movie.director}</strong> 执导，
                {movie.actors.join("、")} 联袂出演，于 {movie.year} 年上线，属于{movie.region}
                {movie.typeLabel}，题材涵盖 {movie.genres.join("、")}。
              </p>
            </section>

            {isTv && movie.episodes && (
              <div className="mt-8">
                <EpisodeList
                  episodes={movie.episodes}
                  activeIndex={episodeIndex}
                  onSelect={setEpisodeIndex}
                />
              </div>
            )}

            {related.length > 0 && (
              <section className="mt-10">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                  <i className="ri-movie-2-line text-xl text-primary-500"></i>
                  相关推荐
                </h2>
                <div className="mt-4">
                  <MovieGrid movies={related} columns={3} />
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      <Toast message={message} />
    </Layout>
  );
}